import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { resolveBindings } from '@shared/bindings';
import { BindingsContext } from '../state/BindingsContext';
import { useExperimentalEngines } from '../state/FeatureFlagsContext';
import type { PageProps } from './types';
import { MomentumV3ArchetypeBody } from './momentum-v3/registry';
import { StudioPage } from './studio/StudioPage';
import { ForgePage } from './forge/ForgePage';

export {
  AudienceProfilesPage,
  BudgetSummaryPage,
  CampaignArchitecturePage,
  CoverPage,
  CustomerJourneyPage,
  GeoStrategyPage,
  MediaMixPage,
  MonthlySchedulePage,
  OverviewPage,
  ProductDividerPage,
  ProductEducationPage,
  ProductPlanPage,
  ReportingPage,
  StrategyPage,
  TacticAudiencePage,
  TacticGroupPage,
  TacticMeasurementPage,
  TacticMomentPage,
  TermsPage,
  ThankYouPage,
  WhyUsPage,
} from './momentum-v3/registry';

/**
 * Dispatches one DocumentSection to its page archetype by type. One section
 * is always exactly one page - see design.md's "one section = one page".
 * Legacy `media_product` sections render through the product_plan archetype
 * (same component; it already reads `section.content.rationale/body` either
 * way), same as the V5 `product_plan` bundle page.
 *
 * Also the single mount point for {{binding}} resolution: every surface
 * (grid, gallery, share, print) renders pages through here, so
 * display text and editor drafts both resolve against the proposal
 * (Editable reads BindingsContext).
 *
 * Momentum 3 (`momentum_v3`) is now the ONLY deck design (product decision:
 * `standard_v1`/`momentum_v1`/`momentum_v2` are sunset from the web app).
 * Every `document.theme.templateId` value - including any legacy id still
 * sitting in stored proposal data - renders through `MomentumV3ArchetypeBody`
 * wrapped in the `.tpl-momentum-v3` scope class that styles.css keys every
 * momentum_v3 rule off of. The narrower `TemplateId` union in
 * `src/domain/types.ts` is untouched by this - a later stream migrates
 * stored data and tightens the server/state types; this web-only change just
 * always renders the one remaining design regardless of what value is on
 * the record.
 *
 * Build Engine Selector v1 (spec §59-60; NOW SHELVED behind
 * PROPOSAL_EXPERIMENTAL_ENGINES — see src/build-engines/README.md): this is
 * also the single dispatch point for RENDER MODE, orthogonal to template
 * family, gated on the `experimentalEngines` feature flag
 * (state/FeatureFlagsContext). When that flag is off, `isStudio`/`isForge`
 * below are always false, so a section with `renderMode: 'studio' |
 * 'studio-safe' | 'forge'` (persisted from before the flag was shelved)
 * falls straight through to `MomentumV3ArchetypeBody` and dispatches on
 * `section.type` exactly like a classic section — the design intent is
 * that Studio/Forge sections keep their classic identity fields (type,
 * content) intact for exactly this fallback; a section type the family
 * switch doesn't recognise already renders through `GenericPage`, its own
 * "classic-fallback".
 *
 * When the flag is ON: a section built by the Studio engine
 * (`section.renderMode === 'studio' | 'studio-safe'` with a resolved scene
 * graph already on it) renders through `StudioPage` regardless of
 * `document.theme.templateId` — Studio mounts the momentum_v3 `Frame` chrome
 * itself (repointed from momentum_v2, its own byte-for-byte source), so it
 * gets the `.tpl-momentum-v3` scope class the same way an actual momentum_v3
 * section does. A section built by the Forge engine (`section.renderMode
 * === 'forge'` with a generated SVG already on it) renders through
 * `ForgePage` the same way. Forge's OWN fallback targets reuse the existing
 * render modes unchanged — a Forge slide that fell back to the safe scene
 * graph carries `renderMode: 'studio-safe'` (and `studio.resolved`), so it
 * renders through `StudioPage` exactly like a Studio-native fallback; a
 * Forge slide that fell back to the classic template carries `renderMode:
 * 'classic-fallback'` and renders through `MomentumV3ArchetypeBody`. Every
 * other section (no renderMode, 'classic', or 'classic-fallback') renders
 * through `MomentumV3ArchetypeBody`, byte-for-byte unchanged.
 */
/**
 * Which shelved-engine render mode (if any) should win for this section —
 * hooks-free so it's unit-testable without a React renderer (see
 * test/web/registry-render-mode.test.ts). `null` means "fall through to the
 * ordinary template-family switch", which is always what happens when
 * `experimentalEngines` is false, no matter what the section's persisted
 * renderMode says.
 */
export function resolveExperimentalRenderMode(
  section: Pick<PageProps['section'], 'renderMode' | 'studio' | 'forge'>,
  experimentalEngines: boolean,
): 'studio' | 'forge' | null {
  if (!experimentalEngines) return null;
  if (section.renderMode === 'forge' && Boolean(section.forge?.svg)) return 'forge';
  if (
    (section.renderMode === 'studio' || section.renderMode === 'studio-safe')
    && Boolean(section.studio?.resolved)
  ) {
    return 'studio';
  }
  return null;
}

export function PageArchetype(props: PageProps): ReactElement {
  const resolve = useMemo(
    () => (text: string) => resolveBindings(text, props.proposal),
    [props.proposal],
  );
  const experimentalEngines = useExperimentalEngines();
  const renderMode = resolveExperimentalRenderMode(props.section, experimentalEngines);
  const isStudio = renderMode === 'studio';
  const isForge = renderMode === 'forge';
  return (
    <BindingsContext.Provider value={resolve}>
      {isForge ? (
        <div className="tpl-momentum-v3 tpl-forge">
          <ForgePage {...props} />
        </div>
      ) : isStudio ? (
        <div className="tpl-momentum-v3 tpl-studio">
          <StudioPage {...props} />
        </div>
      ) : (
        <div className="tpl-momentum-v3">
          <MomentumV3ArchetypeBody {...props} />
        </div>
      )}
    </BindingsContext.Provider>
  );
}
